import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDeck } from "../../lib/deck-context";
import { fmtM, fmtMoney, fmtPct } from "../../lib/model";
import { Card, Eyebrow, Note, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";
import { STAGES, stageNumbers } from "./exitData";
import { StageDetail } from "./ExitDetail";
import { ExitComparison } from "./ExitComparison";

const Ladder = ({ sel, setSel }) => {
  const { m, inputs } = useDeck();
  return (
    <div data-testid="exit-value-ladder" className="grid grid-cols-1 md:grid-cols-5 gap-3 md:items-end">
      {STAGES.map((s, i) => {
        const n = stageNumbers(s, m, inputs);
        const active = sel === i;
        return (
          <motion.button
            key={s.key}
            data-testid={`ladder-step-${s.key}`}
            onClick={() => setSel(i)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08, duration: 0.5 }}
            className={`text-left rounded-[14px] border p-6 transition-colors duration-300 flex flex-col justify-between ${active ? "bg-arq border-arq text-white" : "bg-white border-line hover:border-arq/50"}`}
            style={{ minHeight: 190 + i * 46 }}
          >
            <div>
              <div className={`font-mono text-[10px] tracking-[0.2em] ${active ? "text-white/70" : "text-arq"}`}>EXIT {s.n}</div>
              <div className={`mt-2 font-display text-xl leading-tight ${active ? "text-white" : "text-ink"}`}>{s.title}</div>
            </div>
            <div className="mt-6">
              <div className={`text-[11px] ${active ? "text-white/70" : "text-steel"}`}>Estimated profit</div>
              <div data-testid={`ladder-profit-${s.key}`} className="font-display text-3xl tabular-nums">{fmtMoney(n.created)}</div>
              <div className={`mt-1 text-[11px] tabular-nums ${active ? "text-white/70" : "text-steel"}`}>{s.key === "hold" ? `${fmtM(n.value)} cash-out + ownership` : `Exit value ${fmtM(n.value)}`}</div>
              <div className={`mt-3 pt-3 border-t text-xs tabular-nums flex justify-between ${active ? "border-white/25 text-white/75" : "border-line text-steel"}`}><span>Cash in {fmtMoney(n.cash)}</span><span>~{n.months} mo</span></div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

export default function Exit() {
  const [sel, setSel] = useState(3);
  const { m } = useDeck();
  return (
    <Section testid="section-exit">
      <SectionHeader num="10" eyebrow="Exit Strategy" title="Development Value Ladder" lede="Five monetization points — from assigning a protected development position to long-term ownership. Each step up spends more capital, retires more risk and creates more value." status="preliminary" />
      <Reveal><Ladder sel={sel} setSel={setSel} /></Reveal>

      <Reveal delay={0.1} className="mt-12">
        <div data-testid="exit-tabs" className="inline-flex flex-wrap gap-1 rounded-full border border-line bg-white p-1">
          {STAGES.map((s, i) => (
            <button key={s.key} data-testid={`exit-tab-${s.key}`} onClick={() => setSel(i)} className={`rounded-full px-5 py-2 text-sm transition-colors duration-200 ${sel === i ? "bg-ink text-white" : "text-char hover:bg-paper"}`}>
              {s.short}
            </button>
          ))}
        </div>
      </Reveal>
      <AnimatePresence mode="wait">
        <motion.div key={sel} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="mt-6">
          <StageDetail s={STAGES[sel]} />
        </motion.div>
      </AnimatePresence>

      <ExitComparison />

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Note testid="exit-disclaimer">
          <div className="mb-2"><StatusBadge s="preliminary" /></div>
          Early-stage assignment values are residual underwriting estimates for planning purposes only. They are not appraisals, offers or guaranteed sale prices.
        </Note>
        <Note>
          Residual method: base stabilized value ({fmtM(m.value)}) − remaining Total Development Cost − buyer profit reserve (35% / 30% / 25% of remaining cost). Actual value depends on City rights, assignability, approvals, leasing and buyer competition. Profit on cost at Build + Sell: {fmtPct(m.poc * 100, 1)}.
        </Note>
      </div>
    </Section>
  );
}
